'use client';

import { useMemo } from 'react';
import type { MobileEvidence } from '@/shared/types';
import {
  citationGroupColumns,
  flattenGroupRow,
  formatGroupCell,
  humanizeGroupField,
} from '@/shared/mobile-service/lib/citation-table';

/**
 * Citation-pane table for a hydrated ``groupBy`` query: key fields, count, and
 * min/max rollups. Unlike {@link CitedTable} this does not use artifact columns.
 */
export function CitedGroups({
  rows,
  label,
  total,
  offset,
  onOffsetChange,
}: {
  rows: Record<string, unknown>[];
  label?: string | null;
  evidence?: MobileEvidence;
  total?: number;
  offset?: number;
  onOffsetChange?: (offset: number) => void;
}) {
  const flatRows = useMemo(() => rows.map(flattenGroupRow), [rows]);
  const columns = useMemo(() => citationGroupColumns(flatRows), [flatRows]);
  const rowCount = total ?? flatRows.length;
  const pageOffset = offset ?? 0;
  const canPage = onOffsetChange != null && total != null;
  const subtitle = `${rowCount} ${rowCount === 1 ? 'group' : 'groups'}`;

  return (
    <div
      data-testid="CitationCitedGroups"
      className="flex min-w-0 w-full shrink-0 flex-col rounded-md bg-white/5"
    >
      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-amber-400/40 bg-amber-400/10 px-3 py-2">
        <div className="min-w-0">
          <p className="m-0 truncate text-sm text-amber-100">
            {label?.trim() || 'Grouped results'}
          </p>
          <p className="m-0 text-xs text-amber-100/70">{subtitle}</p>
        </div>
        {canPage ? (
          <div className="flex shrink-0 items-center gap-2 text-xs text-amber-100/80">
            <button
              type="button"
              disabled={pageOffset <= 0}
              onClick={() => onOffsetChange(Math.max(0, pageOffset - flatRows.length))}
              className="rounded px-2 py-1 enabled:hover:bg-white/10 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={pageOffset + flatRows.length >= rowCount}
              onClick={() => onOffsetChange(pageOffset + flatRows.length)}
              className="rounded px-2 py-1 enabled:hover:bg-white/10 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        ) : null}
      </header>
      <div data-testid="CitationCitedGroupsBody" className="min-w-0 overflow-x-auto p-2">
        {columns.length === 0 ? null : (
          <table className="w-full border-collapse text-left text-xs text-white/90">
            <thead>
              <tr>
                {columns.map((field) => (
                  <th
                    key={field}
                    className="sticky top-0 border-b border-white/15 bg-black/20 px-2 py-1.5 font-medium text-white/70"
                  >
                    {humanizeGroupField(field)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {flatRows.map((row, index) => (
                <tr key={index} className="border-b border-white/10 last:border-b-0">
                  {columns.map((field) => (
                    <td key={field} className="px-2 py-1.5 align-top">
                      {formatGroupCell(row[field])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
